import {
  ApplicationConfig,
  LOCALE_ID,
  ErrorHandler,
  APP_INITIALIZER,
  importProvidersFrom
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';

// NgRx
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';

// Nebular
import {
  NbMenuService,
  NbThemeModule,
  NbLayoutModule,
  NbToastrModule,
  NbMenuModule,
  NbDialogModule,
  NbDatepickerModule,
  NbButtonModule,
  NbIconModule,
  NbSelectModule,
  NbOptionModule,
  NbButtonGroupModule,
  NbSidebarModule
} from '@nebular/theme';

import { NbEvaIconsModule } from '@nebular/eva-icons';
import { NbMomentDateModule } from '@nebular/moment';

// Translate
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// Third party
import { NgCircleProgressModule } from 'ng-circle-progress';
import { EditorModule } from '@tinymce/tinymce-angular';

// App modules
import { routes } from './app.routes';

// Store
import { authReducer } from './shared/store/user/auth.reducer';
import { citiesReducer } from './shared/store/city/city.reducer';
import { AuthEffects } from './shared/store/user/auth.effect';
import { CityEffects } from './shared/store/city/city.effect';

// Services & Interceptors
import { VideoRecordingService } from './pv-modules/pv-recruitment/process-steps/recruitement-interview-step/video-recording.service';
import { JwtInterceptor } from './shared/interceptors/jwt-interceptor';
import { ErrorHandlerInterceptor } from './shared/interceptors/error-handler-interceptor';
import { GlobalErrorHandler } from './shared/services/global-error-handler.service';
import { UntypedFormBuilder } from '@angular/forms';
import { environment } from '../environments/environment';
import { MomentPipePipe } from './common-modules/common-pipes/pipes/moment-pipe.pipe';
import { CommonRoleTranslatePipe } from './common-modules/common-translate/pipes/common-role-translate.pipe';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

function initializeGoogleMaps(): () => void {
  return () => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places&language=fr`;
    document.head.appendChild(script);
  };
}

registerLocaleData(localeFr);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    MomentPipePipe,
    CommonRoleTranslatePipe,

    // 1. Load Main Modules
    importProvidersFrom(
      NbThemeModule.forRoot({ name: 'default' }),
      NbLayoutModule,
      NbSidebarModule.forRoot(),
      NbToastrModule.forRoot(),
      NbEvaIconsModule,
      NbMenuModule.forRoot(),
      NbDialogModule.forRoot(),
      NbDatepickerModule.forRoot(),
      
      NbButtonModule,
      NbIconModule,
      NbSelectModule,
      NbOptionModule,
      NbButtonGroupModule,
      StoreModule.forRoot({ user: authReducer, cities: citiesReducer }),
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
      EffectsModule.forRoot([AuthEffects, CityEffects]),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      NgCircleProgressModule.forRoot(),
      EditorModule
    ),

    importProvidersFrom(NbMomentDateModule),

    // Services
    NbMenuService,
    VideoRecordingService,
    UntypedFormBuilder,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: LOCALE_ID, useValue: 'fr' },
    { provide: APP_INITIALIZER, useFactory: initializeGoogleMaps, multi: true },
  ]
};